import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import {
  setSortBy,
  setSortOrder,
  setVisibleColumns,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const SUPPLIER_SORT_FIELDS = {
  name: "S_Name",
  email: "S_Email",
  contact: "S_Contact",
  city: "S_City",
  postcode: "Post_Code",
  active: "Is_Active",
};

const SUPPLIER_SORT_LABELS = {
  [SUPPLIER_SORT_FIELDS.name]: "Name",
  [SUPPLIER_SORT_FIELDS.email]: "Email",
  [SUPPLIER_SORT_FIELDS.contact]: "Contact",
  [SUPPLIER_SORT_FIELDS.city]: "City",
  [SUPPLIER_SORT_FIELDS.postcode]: "Post Code",
  [SUPPLIER_SORT_FIELDS.active]: "Active",
};

const ManageSuppliers = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "suppliers";
  const defaultVisibleColumns = {
    email: true,
    contact: true,
    city: true,
    postcode: true,
    active: true,
    address: false,
    country: false,
    description: false,
    note: false,
    dateAdded: false,
  };

  const formatDate = (dateVal) => {
    if (dateVal == null || dateVal === "") return "—";
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "S_ID", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const lastSelectedIndex = useRef(null);
  const suppliersFetchSeqRef = useRef(0);
  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: SUPPLIER_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const DeleteSupplier = async (supplier) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${supplier.sName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_SUPPLIER_BY_ID, {
          params: {
            CompID,
            S_ID: supplier.s_ID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setSuppliers((prevGroups) =>
              prevGroups.filter((delGroup) => delGroup.s_ID !== supplier.s_ID)
            );
            showSuccessToast("Supplier is Deleted.");
          } else showErrorToast(response.data.message);
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (supplierId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (suppliers[i]?.s_ID) {
            newSelected.add(suppliers[i].s_ID);
          }
        }
      } else {
        if (newSelected.has(supplierId)) {
          newSelected.delete(supplierId);
        } else {
          newSelected.add(supplierId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(suppliers.map((item) => item.s_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No suppliers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected suppliers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((supplierId) =>
          axiosInstance.delete(APIs.DELETE_SUPPLIER_BY_ID, {
            params: {
              CompID,
              S_ID: supplierId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchSuppliers();
      showSuccessToast(`${selectedItems.size} suppliers deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected suppliers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No suppliers selected for editing.");
      return;
    }

    const selectedSupplierIds = Array.from(selectedItems);
    navigate(`/supplier/bulkEdit`, { state: { selectedSupplierIds } });
  };

  const fetchSuppliers = useCallback(async () => {
    const fetchSeq = ++suppliersFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_SUPPLIERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
        },
      });

      if (fetchSeq !== suppliersFetchSeqRef.current) return;

      const suppliersData = response.data.supplier || [];
      const records = response.data.totalRecord || 0;

      setTotalPages(response.data.pageCount || 0);
      setTotalRecords(records);

      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      } else {
        setSuppliers([]);
      }

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (error) {
      if (fetchSeq !== suppliersFetchSeqRef.current) return;
      setSuppliers([]);
      setTotalPages(0);
      setTotalRecords(0);
      showErrorToast("Failed to fetch suppliers.");
    } finally {
      if (fetchSeq === suppliersFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy, sortOrder, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(suppliersFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchSuppliers(), [fetchSuppliers]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageNumber, pageSize?.value, sortBy?.value, sortOrder?.value]);

  const GoToEdit = (s_ID) => navigate(`/supplier/updateSupplier/${s_ID}`);

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No suppliers match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no suppliers to show right now. Add a new supplier or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || suppliers.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No suppliers found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Supplier</li>
          </ol>
          <h1 className="page-header mb-0">Manage Supplier</h1>
        </div>

        <div className="ms-auto">
          <Link to="/supplier/AddSupplier">
            <ThemeButton icon="fa fa-plus">Add Supplier</ThemeButton>
          </Link>
        </div>
      </div>
      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton
            onClick={() => navigate("/supplier/AddSupplier")}
            title="Add Supplier"
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Suppliers"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, email, contact..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {suppliers.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  suppliers.length > 0 &&
                                  selectedItems.size === suppliers.length
                                }
                              />
                            </th>
                          )}
                          <SortableColumnHeader
                            sortField={SUPPLIER_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                            className="text-start"
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.email && (
                            <SortableColumnHeader
                              sortField={SUPPLIER_SORT_FIELDS.email}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-start"
                            >
                              Email
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.contact && (
                            <SortableColumnHeader
                              sortField={SUPPLIER_SORT_FIELDS.contact}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-start"
                            >
                              Contact
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.city && (
                            <SortableColumnHeader
                              sortField={SUPPLIER_SORT_FIELDS.city}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-start"
                            >
                              City
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.postcode && (
                            <SortableColumnHeader
                              sortField={SUPPLIER_SORT_FIELDS.postcode}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-start"
                            >
                              Post Code
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.active && (
                            <SortableColumnHeader
                              sortField={SUPPLIER_SORT_FIELDS.active}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-start"
                            >
                              Active
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.address && (
                            <th className="pt-2 pb-2 text-start">Address</th>
                          )}
                          {visibleColumns.country && (
                            <th className="pt-2 pb-2 text-start">Country</th>
                          )}
                          {visibleColumns.description && (
                            <th className="pt-2 pb-2 text-start">Description</th>
                          )}
                          {visibleColumns.note && (
                            <th className="pt-2 pb-2 text-start">Note</th>
                          )}
                          {visibleColumns.dateAdded && (
                            <th className="pt-2 pb-2 text-start">Date Added</th>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {suppliers.map((supplier, index) => (
                          <tr key={supplier.s_ID}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(supplier.s_ID)}
                                  onChange={(e) =>
                                    handleSelectItem(supplier.s_ID, index, e)
                                  }
                                  data-chkbox-shiftsel="type1"
                                />
                              </td>
                            )}
                            <td
                              className="align-middle text-start"
                              onClick={() => GoToEdit(supplier.s_ID)}
                              style={{ cursor: "pointer" }}
                            >
                              {supplier.s_Name}
                            </td>
                            {visibleColumns.email && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_Email || "—"}
                              </td>
                            )}
                            {visibleColumns.contact && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_Contact || "—"}
                              </td>
                            )}
                            {visibleColumns.city && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_City || "—"}
                              </td>
                            )}
                            {visibleColumns.postcode && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.post_Code || supplier.Post_Code || "—"}
                              </td>
                            )}
                            {visibleColumns.active && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {(supplier.is_Active ?? supplier.Is_Active) ? "Yes" : "No"}
                              </td>
                            )}
                            {visibleColumns.address && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {[supplier.s_Address1 || supplier.S_Address1, supplier.s_Address2 || supplier.S_Address2].filter(Boolean).join(", ") || "—"}
                              </td>
                            )}
                            {visibleColumns.country && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_Country || supplier.S_Country || "—"}
                              </td>
                            )}
                            {visibleColumns.description && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer", maxWidth: "200px" }}
                                title={supplier.s_Description || supplier.S_Description || ""}
                              >
                                {(() => {
                                  const desc = supplier.s_Description || supplier.S_Description || "";
                                  if (!desc) return "—";
                                  return desc.length > 50 ? `${desc.slice(0, 50)}…` : desc;
                                })()}
                              </td>
                            )}
                            {visibleColumns.note && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer", maxWidth: "200px" }}
                                title={supplier.s_Note || supplier.S_Note || ""}
                              >
                                {(() => {
                                  const note = supplier.s_Note || supplier.S_Note || "";
                                  if (!note) return "—";
                                  return note.length > 50 ? `${note.slice(0, 50)}…` : note;
                                })()}
                              </td>
                            )}
                            {visibleColumns.dateAdded && (
                              <td
                                className="align-middle text-start"
                                onClick={() => GoToEdit(supplier.s_ID)}
                                style={{ cursor: "pointer" }}
                              >
                                {formatDate(supplier.s_DateTime || supplier.S_DateTime)}
                              </td>
                            )}
                            <td className="align-middle text-start">
                              <TableActionsMenu menuStyle={{ zIndex: 1050 }}>
                                <TableActionsItem
                                  as={Link}
                                  to={`/supplier/updateSupplier/${supplier.s_ID}`}
                                  icon="fa fa-edit"
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => DeleteSupplier(supplier)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default ManageSuppliers;
